import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import umService from "../../services/university-admin/umService";
import { useNotification } from "../../context/NotificationContext";
import { useUniAdminAuth } from "../../context/UniAdminAuthContext";
import { PERMISSIONS } from "../../utils/permissionKeys";
import AppButton from "../../components/ui/AppButton";
import AppTable from "../../components/ui/AppTable";
import ListToolbar from "../../components/ui/ListToolbar";

const UMClubList = ({
  onCreate,
  onEdit,
  onView,
  onManageMembers,
  onManageEvents,
  refreshTrigger,
}) => {
  const { t } = useTranslation("clubs");
  const { t: tCommon } = useTranslation("common");
  const { showSuccess, showError } = useNotification();
  const { can } = useUniAdminAuth();

  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCampus, setSelectedCampus] = useState("");
  const [statusFilter, setStatusFilter] = useState(true);

  const [campuses, setCampuses] = useState([]);
  const cache = useRef({});

  const canFilterByCampus = can(PERMISSIONS.campus.list);

  useEffect(() => {
    if (!canFilterByCampus) return;
    umService.getAllCampuses({}).then((res) => {
      setCampuses(res.success ? res.data || [] : []);
    });
  }, [canFilterByCampus]);

  useEffect(() => {
    invalidateCache();
    loadClubs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    pageSize,
    searchTerm,
    selectedCampus,
    statusFilter,
    refreshTrigger,
  ]);

  const invalidateCache = () => {
    cache.current = {};
  };

  const loadClubs = async () => {
    const key = `${statusFilter}|${selectedCampus}|${currentPage}|${pageSize}|${searchTerm}`;

    if (cache.current[key]) {
      const cached = cache.current[key];
      setClubs(cached.content);
      setTotalPages(cached.totalPages);
      setTotalElements(cached.totalElements);
      return;
    }

    try {
      setLoading(true);
      const response = await umService.getClubs({
        page: currentPage,
        size: pageSize,
        search: searchTerm,
        campusId: selectedCampus || undefined,
        status: statusFilter,
      });

      if (response.success) {
        const data = {
          content: response.data.content || [],
          totalPages: response.data.totalPages || 0,
          totalElements: response.data.totalElements || 0,
        };
        cache.current[key] = data;
        setClubs(data.content);
        setTotalPages(data.totalPages);
        setTotalElements(data.totalElements);
      } else {
        setClubs([]);
        setTotalPages(0);
        setTotalElements(0);
      }
    } catch (error) {
      console.error("Error loading clubs:", error);
      setClubs([]);
      setTotalPages(0);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const isActive = Boolean(status);
    return (
      <span
        className={`badge ${
          isActive ? "bg-success" : "bg-danger"
        } bg-opacity-10 text-${isActive ? "success" : "danger"} fw-bold`}
      >
        <i
          className={`fas ${isActive ? "fa-check-circle" : "fa-times-circle"} me-1`}
        ></i>
        {isActive ? tCommon("status.active") : tCommon("status.inactive")}
      </span>
    );
  };

  const handleDelete = async (club) => {
    if (!window.confirm(t("messages.confirmDelete"))) return;

    try {
      setActionLoading((prev) => ({ ...prev, [club.id]: "delete" }));
      const response = await umService.deleteClub({ id: club.id });

      if (response.success) {
        showSuccess(t("messages.deleteSuccess"));
        invalidateCache();
        loadClubs();
      } else {
        showError(t("messages.deleteFailed"));
      }
    } catch (error) {
      console.error("Error deleting club:", error);
      showError(t("messages.deleteFailed"));
    } finally {
      setActionLoading((prev) => ({ ...prev, [club.id]: null }));
    }
  };

  const handleReactivate = async (club) => {
    if (
      !window.confirm(
        t("messages.confirmReactivate", {
          name: club.name || club.nameEn || club.nameLocal,
        }),
      )
    )
      return;

    try {
      setActionLoading((prev) => ({ ...prev, [club.id]: "reactivate" }));
      const response = await umService.updateClub({
        id: club.id,
        data: {
          status: true,
          active: true,
        },
      });

      if (response.success) {
        showSuccess(t("messages.reactivateSuccess"));
        invalidateCache();
        loadClubs();
      } else {
        showError(t("messages.reactivateFailed"));
      }
    } catch (error) {
      console.error("Error reactivating club:", error);
      showError(t("messages.reactivateFailed"));
    } finally {
      setActionLoading((prev) => ({ ...prev, [club.id]: null }));
    }
  };

  const handlePageChange = (newPage) => setCurrentPage(newPage);

  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value, 10));
    setCurrentPage(0);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedCampus("");
    setStatusFilter(true);
    setCurrentPage(0);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const startPage = Math.max(0, currentPage - 2);
    const endPage = Math.min(totalPages - 1, currentPage + 2);

    for (let i = startPage; i <= endPage; i += 1) {
      pages.push(
        <AppButton
          key={i}
          variant={i === currentPage ? "primary" : "outline"}
          className="btn-sm mx-1"
          onClick={() => handlePageChange(i)}
        >
          {i + 1}
        </AppButton>,
      );
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted">
          {tCommon("pagination.showingEntries", {
            start: currentPage * pageSize + 1,
            end: Math.min((currentPage + 1) * pageSize, totalElements),
            total: totalElements,
          })}
        </div>
        <div className="d-flex align-items-center">
          <AppButton
            variant="outline"
            className="btn-sm me-2"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 0}
          >
            {tCommon("buttons.previous")}
          </AppButton>
          {pages}
          <AppButton
            variant="outline"
            className="btn-sm ms-2"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages - 1}
          >
            {tCommon("buttons.next")}
          </AppButton>
        </div>
      </div>
    );
  };

  const isDeleted = statusFilter === false;
  const hasActiveFilters =
    searchTerm || selectedCampus || statusFilter === false;

  return (
    <div className="club-list">
      <ListToolbar
        title={t("title")}
        subtitle={t("clubsFound", { count: totalElements })}
        filterBadges={
          hasActiveFilters ? (
            <>
              {searchTerm && (
                <span className="badge bg-primary">Search: "{searchTerm}"</span>
              )}
              {selectedCampus && (
                <span className="badge bg-success">
                  {t("filterByCampus")}:{" "}
                  {
                    campuses.find(
                      (c) => String(c.id) === String(selectedCampus),
                    )?.nameEn
                  }
                </span>
              )}
              {statusFilter === false && (
                <span className="badge bg-danger">{t("filterDeleted")}</span>
              )}
            </>
          ) : null
        }
      >
        <div className="row g-3">
          <div className="col-md-4">
            <label className="form-label fw-medium">{t("searchClubs")}</label>
            <div className="input-group">
              <span className="input-group-text bg-light border-end-0">
                <i className="fas fa-search text-muted"></i>
              </span>
              <input
                type="text"
                className="form-control border-start-0"
                placeholder={t("searchByClubName")}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(0);
                }}
              />
              {searchTerm && (
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={() => {
                    setSearchTerm("");
                    setCurrentPage(0);
                  }}
                  title={tCommon("buttons.clear")}
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
          </div>

          {canFilterByCampus && (
            <div className="col-md-3">
              <label className="form-label fw-medium">
                {t("filterByCampus")}
              </label>
              <select
                className="form-select"
                value={selectedCampus}
                onChange={(e) => {
                  setSelectedCampus(e.target.value);
                  setCurrentPage(0);
                }}
              >
                <option value="">{t("allCampuses")}</option>
                {campuses.map((campus) => (
                  <option key={campus.id} value={campus.id}>
                    {campus.nameEn || campus.nameLocal}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="col-md-3 d-flex align-items-end justify-content-md-end gap-2 flex-wrap">
            {hasActiveFilters && (
              <AppButton
                variant="outline"
                className="btn-sm"
                onClick={clearFilters}
              >
                <i className="fas fa-times me-1"></i>
                {tCommon("buttons.clearFilters")}
              </AppButton>
            )}
            <div className="btn-group" role="group">
              <button
                type="button"
                className={`btn btn-sm ${statusFilter === true ? "btn-primary" : "btn-outline-secondary"}`}
                onClick={() => {
                  setStatusFilter(true);
                  setCurrentPage(0);
                }}
              >
                <i className="fas fa-check-circle me-1"></i>
                {t("filterActive")}
              </button>
              <button
                type="button"
                className={`btn btn-sm ${statusFilter === false ? "btn-danger" : "btn-outline-secondary"}`}
                onClick={() => {
                  setStatusFilter(false);
                  setCurrentPage(0);
                }}
              >
                <i className="fas fa-trash-alt me-1"></i>
                {t("filterDeleted")}
              </button>
            </div>
            {!isDeleted && can(PERMISSIONS.club.create) && (
              <AppButton
                variant="primary"
                className="btn-sm"
                onClick={onCreate}
              >
                <i className="fas fa-plus me-1"></i>
                {t("addClub")}
              </AppButton>
            )}
          </div>

          <div className="col-md-2 d-flex align-items-end gap-2">
            <div>
              <label className="form-label mb-0">{t("show")}:</label>
              <select
                className="form-select form-select-sm"
                value={pageSize}
                onChange={handlePageSizeChange}
                style={{ width: "auto" }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </div>
      </ListToolbar>

      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">
                  {tCommon("actions.loading")}
                </span>
              </div>
              <p className="text-muted mt-2">{t("loadingClubs")}</p>
            </div>
          ) : clubs.length === 0 ? (
            <div className="text-center py-5">
              <i
                className="fas fa-users text-muted mb-3"
                style={{ fontSize: "3rem" }}
              ></i>
              <h5 className="text-muted">{t("noClubsFound")}</h5>
              <p className="text-muted">
                {hasActiveFilters ? t("adjustFilters") : t("noClubsCreated")}
              </p>
            </div>
          ) : (
            <AppTable responsive>
              <thead>
                <tr>
                  <th className="ps-4">{t("title")}</th>
                  <th>{t("fields.campus")}</th>
                  <th className="text-center">{t("fields.memberCount")}</th>
                  <th className="text-center">{t("fields.eventCount")}</th>
                  <th className="text-center">{t("fields.status")}</th>
                  <th className="text-center pe-4">
                    {tCommon("table.actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {clubs.map((club) => (
                  <tr key={club.id} className="border-bottom">
                    <td className="ps-4">
                      <div className="d-flex align-items-center">
                        <div
                          className="club-logo rounded bg-primary bg-opacity-10 d-flex flex-shrink-0 align-items-center justify-content-center me-3 overflow-hidden"
                          style={{ width: "50px", height: "50px" }}
                        >
                          {club.imageUrl ? (
                            <img
                              src={club.imageUrl}
                              alt={club.name || club.nameLocal || club.nameEn}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                              }}
                            />
                          ) : (
                            <i className="fas fa-users text-primary fs-4"></i>
                          )}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div className="fw-bold text-dark text-truncate">
                            {club.name || club.nameLocal || club.nameEn}
                          </div>
                          <small 
                            className="text-muted"
                            style={{ 
                              display: '-webkit-box', 
                              WebkitLineClamp: 2, 
                              WebkitBoxOrient: 'vertical', 
                              overflow: 'hidden',
                              whiteSpace: 'normal'
                            }}
                          >
                            {club.description ||
                              club.descriptionLocal ||
                              club.descriptionEn ||
                              t("noDescription")}
                          </small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="text-dark fw-medium">
                        {club.campusName || club.campusNameLocal || club.campusNameEn}
                      </div>
                      <small className="text-muted">
                        {club.universityName || club.universityNameLocal || club.universityNameEn}
                      </small>
                    </td>
                    <td className="text-center">
                      <span className="badge bg-info bg-opacity-10 text-info fw-bold">
                        {club.totalMembers || 0}
                      </span>
                    </td>
                    <td className="text-center">
                      <span className="badge bg-success bg-opacity-10 text-success fw-bold">
                        {club.totalEvents || 0}
                      </span>
                    </td>
                    <td className="text-center">
                      {getStatusBadge(club.status)}
                    </td>
                    <td className="text-center pe-4">
                      {isDeleted ? (
                        can(PERMISSIONS.club.update) && (
                          <AppButton
                            variant="outline"
                            className="btn-sm text-success border-success"
                            onClick={() => handleReactivate(club)}
                            disabled={actionLoading[club.id] === "reactivate"}
                            title={t("reactivate")}
                          >
                            {actionLoading[club.id] === "reactivate" ? (
                              <span className="spinner-border spinner-border-sm"></span>
                            ) : (
                              <>
                                <i className="fas fa-undo me-1"></i>
                                {t("reactivate")}
                              </>
                            )}
                          </AppButton>
                        )
                      ) : (
                        <div className="btn-group" role="group">
                          {can(PERMISSIONS.club.get) && (
                            <AppButton
                              variant="outline"
                              className="btn-sm text-info border-info"
                              onClick={() => onView(club)}
                              title={tCommon("buttons.viewDetails")}
                            >
                              <i className="fas fa-eye"></i>
                            </AppButton>
                          )}
                          {can(PERMISSIONS.club.update) && (
                            <AppButton
                              variant="outline"
                              className="btn-sm text-primary border-primary"
                              onClick={() => onEdit(club)}
                              title={tCommon("buttons.edit")}
                            >
                              <i className="fas fa-edit"></i>
                            </AppButton>
                          )}
                          {can(PERMISSIONS.membership.listElevated, club.id) && (
                            <AppButton
                              variant="outline"
                              className="btn-sm text-warning border-warning"
                              onClick={() => onManageMembers(club)}
                              title={t("manageMembers")}
                            >
                              <i className="fas fa-user-friends"></i>
                            </AppButton>
                          )}
                          {can(PERMISSIONS.event.list) && (
                            <AppButton
                              variant="outline"
                              className="btn-sm text-success border-success"
                              onClick={() => onManageEvents(club)}
                              title={t("manageEvents")}
                            >
                              <i className="fas fa-calendar-alt"></i>
                            </AppButton>
                          )}
                          {can(PERMISSIONS.club.delete) && (
                            <AppButton
                              variant="outline"
                              className="btn-sm text-danger border-danger"
                              onClick={() => handleDelete(club)}
                              disabled={actionLoading[club.id] === "delete"}
                              title={tCommon("buttons.delete")}
                            >
                              {actionLoading[club.id] === "delete" ? (
                                <span className="spinner-border spinner-border-sm"></span>
                              ) : (
                                <i className="fas fa-trash"></i>
                              )}
                            </AppButton>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </AppTable>
          )}
        </div>
      </div>

      {renderPagination()}
    </div>
  );
};

export default UMClubList;
